import React, { useState } from "react";
import flightIcon from "../assets/icons/flight.svg";
import { MdLiveTv } from "react-icons/md";
import { MdRestaurant } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { FaDollarSign } from "react-icons/fa";
import { FaSyncAlt } from "react-icons/fa";
import Bag from "../assets/icons/bag.svg";

/**
 * InternationalRateCard
 *
 * A round‑trip result card that mirrors DoubleRateCard's two-leg layout
 * but shows a SINGLE price action panel on the right (like the provided
 * screenshot). Intended for international/round‑trip itineraries where
 * we present one combined price.
 */
function InternationalRateCard(props) {
  const [showDetails, setShowDetails] = useState(false);
  const [showAmenityDropdown, setShowAmenityDropdown] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [checkedFlights, setCheckedFlights] = useState(new Set());

  // Resolve airline logo
  const airlineImg =
    props.airlineImg ||
    "https://content.r9cdn.net/rimg/provider-logos/airlines/v/B6.png?crop=false&width=108&height=92&fallback=default1.png&_v=1d9c582b6d8f9aebe5ff965c9bb916ee";

  // Outbound flight
  const outbound = {
    time: props.time || "6:20 pm – 6:20 am",
    route: props.route || "JFK-LGW",
    stops: props.flightType || "nonstop",
    stopAirport: "",
    duration: props.duration || "7h 00m",
    airline: props.airlineName || "Norse Atlantic UK",
    flightNumber: props.flightNumber || "",
    aircraft: props.aircraft || "",
    departure: {
      time: props.time ? props.time.split(" – ")[0] : "6:20 pm",
      airport: props.route ? props.route.split("-")[0] : "JFK",
      code: props.route ? props.route.split("-")[0] : "JFK",
    },
    arrival: {
      time: props.time ? props.time.split(" – ")[1] : "6:20 am",
      airport: props.route ? props.route.split("-")[1] : "LGW",
      code: props.route ? props.route.split("-")[1] : "LGW",
    },
    flightDuration: props.duration || "7h 00m",
  };

  // Return flight (optional)
  const hasReturn = props.returnTime && props.returnRoute && props.returnDuration;
  const inbound = hasReturn
    ? {
        time: props.returnTime,
        route: props.returnRoute,
        stops: props.returnFlightType || "nonstop",
        stopAirport: "",
        duration: props.returnDuration,
        airline: props.airlineName || "Norse Atlantic UK",
        flightNumber: props.returnFlightNumber || "",
        aircraft: props.returnAircraft || "",
        departure: {
          time: props.returnTime ? props.returnTime.split(" – ")[0] : "",
          airport: props.returnRoute ? props.returnRoute.split("-")[0] : "",
          code: props.returnRoute ? props.returnRoute.split("-")[0] : "",
        },
        arrival: {
          time: props.returnTime ? props.returnTime.split(" – ")[1] : "",
          airport: props.returnRoute ? props.returnRoute.split("-")[1] : "",
          code: props.returnRoute ? props.returnRoute.split("-")[1] : "",
        },
        flightDuration: props.returnDuration,
      }
    : null;

  const handleFlightDetailsClick = () => setShowDetails((v) => !v);
  const handleAmenityDropdownClick = (e) => {
    e.stopPropagation();
    setShowAmenityDropdown((v) => !v);
  };
  const handleCheckboxClick = (index, e) => {
    e.stopPropagation();
    const next = new Set(checkedFlights);
    next.has(index) ? next.delete(index) : next.add(index);
    setCheckedFlights(next);
  };

  const dealsCount = props.dealsCount ?? 17;
  const cabin = props.cabin || "Economy";
  const totalPrice = props.price || props.price1 || "$447";

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 10,
        boxShadow: "0 2px 8px #dbe2ea",
        border: "1px solid #cfd8e3",
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        overflow: "hidden",
        margin: "10px 0",
      }}
    >
      {/* Best inline ad header (only for the very first card when Best is selected) */}
      {props.showBestHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            backgroundColor: '#fff',
            border: '1px solid #E6EBEF',
            borderRadius: 10,
            margin: '10px 8px 6px',
            padding: '12px',
            gap: 16,
            position: 'relative',
          }}
        >
          {/* Left strip */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              backgroundColor: '#1a73e8',
              borderTopLeftRadius: 10,
              borderBottomLeftRadius: 10,
            }}
          />

          {/* Left: airline logo + text */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingLeft: 12, flex: 1 }}>
            <img
              src={airlineImg}
              alt={props.airlineName || 'Airline'}
              style={{ height: 20, objectFit: 'contain' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontWeight: 800, color: '#1b1f23', fontSize: 16 }}>Find the best deals on eDreams</div>
              <div style={{ color: '#667085', fontSize: 13 }}>Compare flights from more than 600 airlines</div>
            </div>
          </div>
          {/* Ad label */}
          <div style={{ position: 'absolute', right: 16, bottom: 8, color: '#6b7280', fontSize: 12 }}>Ad</div>
        </div>
      )}
      {/* Main row */}
      <div style={{ display: "flex", alignItems: "stretch", gap: 0, padding: "0 5px" }}>
        {/* Left: flight details (two rows) */}
        <div style={{ flex: 2, padding: "6px 14px", display: "flex", flexDirection: "column", borderRight: '1px solid #E6EBEF' }}>
          {/* Top bar: Save/Share */}
          <div style={{ padding: "8px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={props.onSave}
                  style={{
                    border: "1px solid #9ba8b0",
                    background: "#f9fafb",
                    borderRadius: 6,
                    padding: "3px 8px",
                    fontWeight: 700,
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                  }}
                >
                  ♡ Save
                </button>
                <button
                  onClick={props.onShare}
                  style={{
                    border: "1px solid #9ba8b0",
                    background: "#f9fafb",
                    borderRadius: 6,
                    padding: "3px 8px",
                    fontWeight: 700,
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                  }}
                >
                  ⇪ Share
                </button>
              </div>
              <div />
            </div>
          </div>

          {/* Flight rows container (hover to open details) */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                padding: "8px 12px",
                borderRadius: 6,
                transition: "background-color 0.2s",
                cursor: "pointer",
                marginLeft: 36,
              }}
              onClick={handleFlightDetailsClick}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#E6EBEF")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              {[outbound, inbound].filter(Boolean).map((f, idx) => (
                <div key={idx} style={{ position: 'relative', display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
                  {/* Row-anchored checkbox for perfect vertical alignment */}
                  <div
                    style={{
                      position: 'absolute',
                      left: -30,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 24,
                      height: 24,
                      backgroundColor: '#fff',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    //   border: '1px solid #E6EBEF',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={checkedFlights.has(idx)}
                      onChange={(e) => handleCheckboxClick(idx, e)}
                      style={{ width: 16, height: 16, margin: 0, cursor: 'pointer' }}
                    />
                  </div>
                  {/* Airline logo */}
                  <img src={airlineImg} alt={props.airlineName || "Airline"} style={{ width: 40, height: 32, objectFit: "contain" }} />

                  {/* Times + airline */}
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "#222" }}>{f.time}</div>
                    <div style={{ fontSize: 14, color: "#666" }}>{f.airline}</div>
                  </div>

                  {/* Stops */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 60 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#222" }}>{f.stops}</div>
                    {f.stops !== "nonstop" && <div style={{ fontSize: 14, color: "#666" }}>{f.stopAirport}</div>}
                  </div>

                  {/* Duration + route */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, minWidth: 80 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "#222" }}>{f.duration}</div>
                    <div style={{ fontSize: 14, color: "#666" }}>{f.route}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Single price panel */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "stretch",
            width: 150,
            padding: "12px 12px",
            gap: 12,
          }}
        >
          {/* Fee boxes using supplied icons */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6 }}>
            <div aria-label="1 carry-on bag included in price" style={{ display: 'flex', alignItems: 'center', border: '1px solid #e3e7ea', borderRadius: 6, padding: '4px 6px', background: '#fff' }}>
              <div style={{ width: 16, height: 16, display: 'flex', alignItems: 'center' }}>
                <svg viewBox="0 0 200 200" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" role="presentation"><path d="M126.65 92.48h-53.3a13.29 13.29 0 00-13.3 13.3v53.4a13.29 13.29 0 0013.3 13.3v6.7h13.3v-6.7h26.7v6.7h13.3v-6.7a13.29 13.29 0 0013.3-13.3v-53.3a13.38 13.38 0 00-13.3-13.4zM88.42 34.12a3.33 3.33 0 013.3-3.3H95v3.3h13.3v-3.3h3.3a3.33 3.33 0 013.3 3.3v53.3h10v-53.3a13.29 13.29 0 00-13.3-13.3H91.72a13.29 13.29 0 00-13.3 13.3v53.3h10z"></path></svg>
              </div>
              <div style={{ marginLeft: 6, fontWeight: 600, fontSize: 12 }}>1</div>
            </div>
            <div aria-label="0 checked bags, not included in price" style={{ display: 'flex', alignItems: 'center', border: '1px solid #e3e7ea', borderRadius: 6, padding: '4px 6px', background: '#fff' }}>
              <div style={{ width: 16, height: 16, display: 'flex', alignItems: 'center' }}>
                <svg viewBox="0 0 200 200" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" role="presentation"><path d="M84.35 40.9h-11.9V25.1a15.83 15.83 0 0115.8-15.8H112a15.83 15.83 0 0115.8 15.8v15.8H116V25.1a3.9 3.9 0 00-3.9-3.9H108v3.9H92.05v-3.9h-3.8a3.9 3.9 0 00-3.9 3.9zm55 7.9h-55.2v17.7L96 78.3v34H68.35v-34l11.8-11.8V48.8h-19.6a15.83 15.83 0 00-15.8 15.8V167a15.83 15.83 0 0015.8 15.8v7.9h15.8v-7.9h47.3v7.3h15.8v-7.3a15.83 15.83 0 0015.8-15.8V64.5a15.9 15.9 0 00-15.9-15.7zm-61.1 27.6a3.9 3.9 0 003.9 3.9a4 4 0 003.9-3.9a3.9 3.9 0 10-7.8 0z"></path></svg>
              </div>
              <div style={{ marginLeft: 6, fontWeight: 600, fontSize: 12 }}>0</div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, paddingLeft: 4 }}>
            <div style={{ color: "#666", fontSize: 12 }}>{dealsCount} deals from</div>
            <div style={{ fontSize: 30, fontWeight: 900, color: "#111" }}>{totalPrice}</div>
            <div style={{ color: "#222", fontSize: 13 }}>{cabin}</div>
          </div>

          <button
            style={{
              background: "linear-gradient(135deg,#ff690f 0%,#e8381b 100%)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 16px",
              fontWeight: 800,
              fontSize: 14,
              cursor: "pointer",
              width: '100%',
            }}
            onClick={props.onSelect}
          >
            {props.showBestHeader ? 'View deal' : 'Select'}
          </button>
        </div>
      </div>

      {/* Details section (shared with DoubleRateCard style) */}
      {showDetails && (
        <>
          <div
            style={{
              background: "#f8f9fa",
              padding: 16,
              margin: 10,
              borderRadius: 10,
            }}
          >
            {/* Outbound details */}
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 2px 8px #dbe2ea",
                border: "1px solid #cfd8e3",
                padding: 16,
                marginBottom: hasReturn ? 12 : 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                  padding: 0,
                  borderBottom: "1px solid #dee2e6",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 16, color: "#333" }}>
                  Depart • {outbound.departure.airport} to {outbound.arrival.airport}
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#666" }}>{outbound.flightDuration}</div>
              </div>

              <div style={{ marginBottom: 0, position: "relative" }}>
                {/* Airline + aircraft + amenities */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <span style={{ color: "#0074d9", fontWeight: 600, fontSize: 14 }}>{outbound.airline}</span>
                  {outbound.flightNumber && <span style={{ fontSize: 12, color: "#5a6872" }}>{outbound.flightNumber}</span>}
                  {outbound.aircraft && (
                    <span
                      style={{
                        color: "#666",
                        padding: "4px 12px",
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 500,
                        border: "1px solid #cfd8e3",
                        marginLeft: 10,
                      }}
                    >
                      {outbound.aircraft}
                    </span>
                  )}

                  <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", position: "relative" }}>
                    {!showAmenityDropdown && (
                      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 8px", background: "#f5f5f5", borderRadius: 16 }}>
                        <span style={{ fontSize: 14, color: "#333" }} title="Wi-Fi">📶</span>
                        <span style={{ fontSize: 14, color: "#333" }} title="In-flight entertainment">
                          <MdLiveTv />
                        </span>
                        <span style={{ fontSize: 14, color: "#333" }} title="Food service">
                          <MdRestaurant />
                        </span>
                        <span
                          style={{ fontSize: 12, color: "#666", cursor: "pointer", marginLeft: 4, transform: showAmenityDropdown ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                          title="More details"
                          onClick={handleAmenityDropdownClick}
                        >
                          ▼
                        </span>
                      </div>
                    )}

                    {showAmenityDropdown && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          background: "#f5f5f5",
                          borderRadius: 6,
                          padding: 8,
                          marginTop: 4,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          zIndex: 10,
                          minWidth: 200,
                        }}
                      >
                        <div style={{ position: "absolute", top: -4, right: 12, width: 8, height: 8, background: "#f5f5f5", transform: "rotate(45deg)" }} />
                        <div style={{ position: "absolute", top: 0, right: 10, fontSize: 10, color: "#666", cursor: "pointer" }} onClick={handleAmenityDropdownClick}>
                          ▲
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14, color: "#333" }}>📶</span>
                            <span style={{ fontSize: 12, color: "#333" }}>Wi‑Fi available</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14, color: "#333" }}>
                              <MdLiveTv />
                            </span>
                            <span style={{ fontSize: 12, color: "#333" }}>Streaming entertainment</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14, color: "#333" }}>
                              <MdRestaurant />
                            </span>
                            <span style={{ fontSize: 12, color: "#333" }}>Meal provided</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vertical timeline */}
                <div style={{ position: "relative", paddingLeft: 30 }}>
                  <div style={{ position: "absolute", left: 14, top: 0, bottom: 0, width: 2, background: "#ddd" }} />

                  {/* depart */}
                  <div style={{ position: "relative", marginBottom: 16 }}>
                    <div style={{ position: "absolute", left: -21, top: 0, width: 8, height: 8, borderRadius: "50%", border: "2px solid #ddd", background: "#fff" }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 18, color: "#333" }}>{outbound.departure.time}</div>
                      <div style={{ fontSize: 16, color: "#666", fontWeight: 500, marginLeft: 30 }}>
                        {outbound.departure.airport} ({outbound.departure.code})
                      </div>
                    </div>
                  </div>

                  {/* duration with airplane */}
                  <div style={{ position: "relative", marginBottom: 16, display: "flex", alignItems: "center" }}>
                    <div style={{ position: "absolute", left: -26, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>
                      <img src={flightIcon} alt="flight" />
                    </div>
                    <div style={{ fontSize: 14, color: "#666" }}>{outbound.duration}</div>
                  </div>

                  {/* arrival */}
                  <div style={{ position: "relative", marginBottom: 8 }}>
                    <div style={{ position: "absolute", left: -21, top: 12, width: 8, height: 8, borderRadius: "50%", border: "2px solid #ddd", background: "#fff" }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 18, color: "#333" }}>{outbound.arrival.time}</div>
                      <div style={{ fontSize: 16, color: "#666", fontWeight: 500, marginLeft: 30 }}>
                        {outbound.arrival.airport} ({outbound.arrival.code})
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Inbound details */}
            {hasReturn && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 10,
                  boxShadow: "0 2px 8px #dbe2ea",
                  border: "1px solid #cfd8e3",
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                    padding: 0,
                    borderBottom: "1px solid #dee2e6",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 16, color: "#333" }}>
                    Return • {inbound.departure.airport} to {inbound.arrival.airport}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#666" }}>{inbound.flightDuration}</div>
                </div>

                <div style={{ marginBottom: 0, position: "relative" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <span style={{ color: "#0074d9", fontWeight: 600, fontSize: 14 }}>{inbound.airline}</span>
                    {inbound.flightNumber && <span style={{ fontSize: 12, color: "#5a6872" }}>{inbound.flightNumber}</span>}
                    {inbound.aircraft && (
                      <span
                        style={{
                          color: "#666",
                          padding: "4px 12px",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 500,
                          border: "1px solid #cfd8e3",
                          marginLeft: 10,
                        }}
                      >
                        {inbound.aircraft}
                      </span>
                    )}
                  </div>

                  <div style={{ position: "relative", paddingLeft: 30 }}>
                    <div style={{ position: "absolute", left: 14, top: 0, bottom: 0, width: 2, background: "#ddd" }} />

                    <div style={{ position: "relative", marginBottom: 16 }}>
                      <div style={{ position: "absolute", left: -21, top: 0, width: 8, height: 8, borderRadius: "50%", border: "2px solid #ddd", background: "#fff" }} />
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#333" }}>{inbound.departure.time}</div>
                        <div style={{ fontSize: 16, color: "#666", fontWeight: 500, marginLeft: 30 }}>
                          {inbound.departure.airport} ({inbound.departure.code})
                        </div>
                      </div>
                    </div>

                    <div style={{ position: "relative", marginBottom: 16, display: "flex", alignItems: "center" }}>
                      <div style={{ position: "absolute", left: -26, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>
                        <img src={flightIcon} alt="flight" />
                      </div>
                      <div style={{ fontSize: 14, color: "#666" }}>{inbound.duration}</div>
                    </div>

                    <div style={{ position: "relative", marginBottom: 8 }}>
                      <div style={{ position: "absolute", left: -21, top: 12, width: 8, height: 8, borderRadius: "50%", border: "2px solid #ddd", background: "#fff" }} />
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#333" }}>{inbound.arrival.time}</div>
                        <div style={{ fontSize: 16, color: "#666", fontWeight: 500, marginLeft: 30 }}>
                          {inbound.arrival.airport} ({inbound.arrival.code})
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Price details footer (matches other cards) */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 16,
              borderTop: "1px solid #e9ecef",
              background: "#fff",
            }}
          >
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                <div style={{ fontSize: 12, color: "#666", fontWeight: 400 }}>{dealsCount} deals from</div>
                <div
                  style={{ position: "relative", display: "flex", alignItems: "center", gap: 6 }}
                  onMouseEnter={() => setShowPriceBreakdown(true)}
                  onMouseLeave={() => setShowPriceBreakdown(false)}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "1px solid #333",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#333",
                      cursor: "default",
                    }}
                  >
                    i
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#222" }}>{totalPrice}</div>

                  {showPriceBreakdown && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        bottom: "calc(100% + 12px)",
                        width: 280,
                        background: "#fff",
                        border: "1px solid #e6e8eb",
                        boxShadow: "0 12px 28px rgba(0,0,0,0.16)",
                        zIndex: 50,
                        overflow: "hidden",
                      }}
                    >
                      <div style={{ position: "absolute", right: 18, top: "100%", width: 14, height: 14, background: "#fff", borderLeft: "1px solid #e6e8eb", borderBottom: "1px solid #e6e8eb", transform: "translateY(-2px) rotate(45deg)" }} />

                      <div style={{ padding: "16px 18px" }}>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#111827", marginBottom: 12 }}>Price breakdown</div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 7a2 2 0 012-2h10l6 6v6a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" stroke="#111827" strokeWidth="1.5"/></svg>
                            <span style={{ color: "#374151", fontSize: 14 }}>Ticket price</span>
                          </div>
                          <div style={{ color: "#111827", fontWeight: 400, fontSize: 14 }}>{totalPrice}</div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <img src={Bag} alt="Carry-on bag" style={{ width: 16, height: 16, filter: "grayscale(100%) contrast(120%)" }} />
                            <span style={{ color: "#374151", fontSize: 14 }}>Carry‑on bag</span>
                          </div>
                          <div style={{ color: "#111827", fontWeight: 400, fontSize: 14 }}>Included</div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <img src={Bag} alt="Checked bag" style={{ width: 16, height: 16, filter: "grayscale(100%) contrast(120%)" }} />
                            <span style={{ color: "#374151", fontSize: 14 }}>Checked bag</span>
                          </div>
                          <div style={{ textAlign: "right", color: "#374151" }}>
                            <div style={{ fontWeight: 400, fontSize: 14 }}>Not Included</div>
                            <div style={{ fontSize: 13, color: "#6b7280" }}>(+$70)</div>
                          </div>
                        </div>

                        <div style={{ color: "#6b7280", fontSize: 12, marginTop: 10 }}>Up to 50lbs per bag • Up to 62in total</div>

                        <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "14px 0" }} />

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ color: "#111827", fontWeight: 700, fontSize: 15 }}>Total</div>
                          <div style={{ color: "#111827", fontWeight: 700, fontSize: 15 }}>{totalPrice}</div>
                        </div>
                      </div>

                      <div style={{ background: "#f5f7f8", borderTop: "1px solid #e6e8eb", padding: "12px 18px", fontSize: 12, color: "#6b7280" }}>
                        *Baggage fees may be estimated and vary across booking sites.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                style={{
                  background: "linear-gradient(90deg, #ff690f 0%, #ff8800 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 20px",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(255, 105, 15, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "scale(1.02)";
                  e.target.style.boxShadow = "0 4px 8px rgba(255, 105, 15, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 2px 4px rgba(255, 105, 15, 0.3)";
                }}
              >
                {props.showBestHeader ? "View deal" : "Select"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default InternationalRateCard;


